import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { useLocation, useParams } from 'react-router';
import { Box, Divider, Tooltip, Typography } from '@mui/material';
import { CountryCode } from 'libphonenumber-js';
import Grid from '@mui/material/Grid';
import Close from '@mui/icons-material/Close';
import { AxiosResponse } from 'axios';
import { Form, Formik, FormikHelpers } from 'formik';
import moment from 'moment';
import { useQuery } from 'src/hooks/useQuery';
import * as Yup from 'yup';
import { MerchantTitle } from 'app/MerchantTitle';
import { getIdentityLabel } from 'components/settings/model/Settings';
import { AgeVerification } from 'components/user/ageVerification';
import {
    clearSignupDataFromSession,
    getOnCloseRedirectUrl,
    getSignupDataFromSession,
    setSignupDataToSession
} from 'components/user/localAuth';
import {
    formatPhoneAsE164,
    getIdentityValidationScheme,
    SignUpDOBData,
    SignUpFormData
} from 'components/user/model/User';
import { MuiAppBar } from 'lib/appbar/MuiAppBar';
import { CheckboxFormField } from 'lib/form/CheckboxFormField';
import { PasswordFormField } from 'lib/form/PasswordFormField';
import { TextFormField } from 'lib/form/TextFormField';
import { Link } from 'lib/Link';
import logger from 'lib/logger';
import { isDefined } from 'lib/typeInference';
import { useAuth } from 'lib/useAuth';
import { useLocalHistory } from 'lib/useLocalHistory';
import { clearErrors, register, setRegisterError } from 'store/auth/authSlice';
import { resetGuestState } from 'store/guest/guestSlice';
import { ApplicationState } from 'store/store';
import { LoadingButton } from 'ui/LoadingButton';
import { ROUTES } from './routes';
import { GoogleSSO } from 'components/user/GoogleSSO';
import { AppleSSO } from 'components/user/AppleSSO';
import { TermsAndConditions } from 'components/user/TermsAndCondition';
import { CountryCodePicker, getDefaultCountry } from 'components/user/CountryCodePicker';

const PREFIX = 'SignUpPage';

const classes = {
    root: `${PREFIX}root`,
    button: `${PREFIX}button`,
    forgot: `${PREFIX}forgot`,
    orContainer: `${PREFIX}orContainer`,
    or: `${PREFIX}or`
};

const Root = styled('div')(({ theme }) => ({
    [`& .${classes.root}`]: {
        padding: theme.spacing(2)
    },
    [`& .${classes.button}`]: {
        height: '48px',
        fontSize: '1.2em'
    },
    [`& .${classes.forgot}`]: {
        margin: theme.spacing(0, 1.75),
        marginTop: theme.spacing(1),
        display: 'block'
    },
    [`& .${classes.orContainer}`]: {
        position: 'relative',
        padding: theme.spacing(0, 5),
        marginBottom: theme.spacing(2),
        marginTop: theme.spacing(2)
    },
    [`& .${classes.or}`]: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: theme.palette.background.default,
        padding: theme.spacing(1, 3),
        color: theme.palette.divider
    }
}));

const TitleTypography = styled(Typography)(({ theme }) => ({
    color: theme.palette.titleTextColour,
    fontWeight: 600,
    fontSize: '1rem',
    padding: theme.spacing(1, 0, 2, 0)
}));

const birthDateInitialValues: SignUpDOBData = {
    birthDate: ''
};

export const SignUpPage: React.FC = () => {
    const { t } = useTranslation();
    const { state: locationState } = useLocation();

    const dispatch = useAppDispatch();
    const paramIdentity = useQuery('identity');
    const paramFirstName = useQuery('firstName');
    const paramLastName = useQuery('lastName');
    const { getParsedPath, push } = useLocalHistory();
    const [isAgePageOpen, setAgePageOpen] = React.useState(false);
    const [signUpData, setSignUpData] = React.useState<SignUpFormData>();
    const [SSOInProgress, setSSOInProgress] = React.useState(false);
    const { merchantId } = useParams<{ merchantId: string }>();
    const { registerError, returnUrl } = useSelector((state: ApplicationState) => state.auth);
    const { isLoggedIn } = useAuth();
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const [selectedCountry, setSelectedCountry] = React.useState<string>(() =>
        getDefaultCountry(
            settings?.phoneAuth?.supportedCountryCallingCodes ?? [],
            settings?.region?.phoneNumberCode
        )
    );
    const initialFields: SignUpFormData = React.useMemo(() => {
        if (!!signUpData) {
            return {
                identity: (paramIdentity || signUpData.identity) ?? '',
                firstName: (paramFirstName || signUpData.firstName) ?? '',
                lastName: (paramLastName || signUpData.lastName) ?? '',
                password: signUpData.password ?? '',
                confirmPassword: signUpData.confirmPassword ?? '',
                hasAgreedToShareData: signUpData.hasAgreedToShareData ?? false,
                hasAgreedToReceiveMarketing: signUpData.hasAgreedToReceiveMarketing ?? false
            };
        }
        return {
            identity: paramIdentity || locationState?.identity || '',
            firstName: paramFirstName || locationState?.user?.firstName || '',
            lastName: paramLastName || locationState?.user?.lastName || '',
            password: '',
            confirmPassword: '',
            hasAgreedToShareData: locationState?.user?.hasAgreedToShareData ?? false,
            hasAgreedToReceiveMarketing: locationState?.user?.hasAgreedToReceiveMarketing ?? false
        };
    }, [
        signUpData,
        paramIdentity,
        locationState?.identity,
        locationState?.user?.firstName,
        locationState?.user?.lastName,
        locationState?.user?.hasAgreedToShareData,
        locationState?.user?.hasAgreedToReceiveMarketing,
        paramFirstName,
        paramLastName
    ]);
    const shouldApplyAgeRequest = React.useMemo(
        () =>
            !!settings &&
            !!settings.app &&
            settings.app.ageRequestEnabled &&
            isDefined(settings.app.registrationMinimumAge),
        [settings]
    );
    const identityLabel = React.useMemo(
        () => getIdentityLabel(settings?.emailAuthEnabled, settings?.phoneAuthEnabled, true),
        [settings?.emailAuthEnabled, settings?.phoneAuthEnabled]
    );

    const showCountryInfo = identityLabel === 'phone';

    const signupValidation = React.useMemo(
        () =>
            Yup.object().shape({
                identity: getIdentityValidationScheme(identityLabel, selectedCountry as CountryCode, t),
                firstName: Yup.string().required(t('FORMS_VALIDATION_REQUIRED')),
                lastName: Yup.string().required(t('FORMS_VALIDATION_REQUIRED')),
                password: Yup.string()
                    .min(8, t('FORMS_VALIDATION_MIN', { count: 8 }))
                    .required(t('FORMS_VALIDATION_REQUIRED')),
                confirmPassword: Yup.string()
                    .oneOf([Yup.ref('password')], t('ERROR_SIGN_UP_REPEAT_PASSWORD'))
                    .required(t('FORMS_VALIDATION_REQUIRED')),
                hasAgreedToShareData: Yup.boolean()
                    .required(t('FORMS_VALIDATION_REQUIRED'))
                    .equals([true], t('FORMS_VALIDATION_REQUIRED'))
            }),
        [identityLabel, t, selectedCountry]
    );

    const birthDateValidation = React.useMemo(
        () =>
            Yup.object().shape({
                birthDate: Yup.string()
                    .required(t('FORMS_VALIDATION_REQUIRED'))
                    .test(
                        'isAgeValid',
                        t('ONBOARDING_DOB_MESSAGE'),
                        (val: string) =>
                            !(
                                !!settings &&
                                !!settings.app &&
                                moment(val)
                                    .add(settings.app.registrationMinimumAge, 'years')
                                    .isAfter(moment(new Date()))
                            )
                    )
            }),
        [settings, t]
    );

    const handleKeyPress = React.useCallback(
        (submit: () => Promise<void>) => (e: React.KeyboardEvent) => {
            if (e.keyCode === 13) {
                submit();
            }
        },
        []
    );
    const handleSubmit = React.useCallback(
        (data: SignUpFormData, { setSubmitting }: FormikHelpers<SignUpFormData>) => {
            const normalizedData: SignUpFormData = {
                ...data,
                identity:
                    identityLabel === 'phone'
                        ? formatPhoneAsE164(data.identity, selectedCountry as CountryCode)
                        : data.identity
            };
            if (shouldApplyAgeRequest) {
                const { firstName, lastName, identity, hasAgreedToShareData, hasAgreedToReceiveMarketing } =
                    normalizedData;
                setSignUpData(normalizedData);
                if (merchantId) {
                    setSignupDataToSession(merchantId, {
                        firstName,
                        lastName,
                        identity,
                        hasAgreedToShareData,
                        hasAgreedToReceiveMarketing
                    });
                }
                setAgePageOpen(true);
                setSubmitting(false);
                return;
            }
            if (merchantId) {
                clearSignupDataFromSession(merchantId);
            }
            dispatch(register(normalizedData))
                .unwrap()
                .then(res => {
                    push(ROUTES.USER.ACTIVATE, {}, '', { user: res });
                })
                .catch(err => {
                    logger.warn(err);
                })
                .finally(() => {
                    setSubmitting(false);
                });
        },
        [dispatch, merchantId, push, shouldApplyAgeRequest, selectedCountry, identityLabel]
    );
    const handleDOBSubmit = React.useCallback(
        (dobValues: SignUpDOBData, helpers: FormikHelpers<SignUpDOBData>) => {
            if (!!signUpData) {
                if (merchantId) {
                    clearSignupDataFromSession(merchantId);
                }
                dispatch(register({ ...signUpData, birthdate: dobValues.birthDate }))
                    .unwrap()
                    .then(res => {
                        push(ROUTES.USER.ACTIVATE, {}, '', { user: res });
                    })
                    .catch(err => {
                        logger.warn(err);
                    })
                    .finally(() => {
                        helpers.setSubmitting(false);
                    });
            }
        },
        [dispatch, merchantId, push, signUpData]
    );

    const handleSSOSuccess = React.useCallback(() => {
        setSSOInProgress(false);
        if (merchantId) {
            clearSignupDataFromSession(merchantId);
        }
    }, [merchantId]);

    const handleSSOError = React.useCallback(
        (e: AxiosResponse<{ message: string }>) => {
            setSSOInProgress(false);
            dispatch(setRegisterError(e.data.message));
        },
        [dispatch]
    );

    const handleSSOStart = React.useCallback(() => {
        setSSOInProgress(true);
    }, []);

    const handleBackFromAgeVerification = React.useCallback(() => {
        setAgePageOpen(false);
    }, []);

    const handleCountryChange = React.useCallback(
        (setFieldValue: FormikHelpers<SignUpFormData>['setFieldValue']) => (value: string) => {
            setSelectedCountry(value);
            setFieldValue('identity', '');
        },
        []
    );
    React.useEffect(() => {
        if (isLoggedIn && !SSOInProgress && merchantId) {
            const onCloseReturnUrl = getOnCloseRedirectUrl(merchantId);
            const returnAddress = returnUrl ?? onCloseReturnUrl;
            if (returnAddress) {
                const [path, search] = returnAddress.split('?');
                push(path, {}, search);
            } else {
                push(ROUTES.BASE);
            }
        }
    }, [SSOInProgress, isLoggedIn, merchantId, push, returnUrl]);

    React.useEffect(() => {
        dispatch(resetGuestState());
        return () => {
            dispatch(clearErrors());
        };
    }, [dispatch]);
    React.useEffect(() => {
        if (!merchantId) {
            return;
        }
        const signupDataFromSessionAsString = getSignupDataFromSession(merchantId);
        if (!!signupDataFromSessionAsString) {
            const { firstName, lastName, identity, hasAgreedToShareData, hasAgreedToReceiveMarketing } =
                JSON.parse(signupDataFromSessionAsString);
            setSignUpData(oldData => ({
                firstName,
                lastName,
                identity,
                hasAgreedToShareData,
                hasAgreedToReceiveMarketing,
                password: oldData?.password ?? '',
                confirmPassword: oldData?.confirmPassword ?? ''
            }));
        }
    }, [merchantId]);

    return (
        <Root>
            {isAgePageOpen ? (
                <AgeVerification
                    error={registerError}
                    onBack={handleBackFromAgeVerification}
                    initialValues={birthDateInitialValues}
                    birthDateValidation={birthDateValidation}
                    onSubmit={handleDOBSubmit}
                />
            ) : (
                <>
                    <MerchantTitle title={t('ONBOARDING_SIGN_UP')} />
                    <MuiAppBar title={t('ONBOARDING_SIGN_UP')} disabledMenu icon={Close} />
                    <Formik
                        initialValues={initialFields}
                        validationSchema={signupValidation}
                        onSubmit={handleSubmit}
                        enableReinitialize
                    >
                        {({
                            submitForm,
                            setFieldValue,
                            isSubmitting,
                            isValid,
                            values: { hasAgreedToShareData }
                        }) => {
                            const countryPicker = (
                                <CountryCodePicker
                                    selectedCountry={selectedCountry}
                                    onCountryChange={handleCountryChange(setFieldValue)}
                                    supportedCodes={settings?.phoneAuth?.supportedCountryCallingCodes ?? []}
                                />
                            );

                            return (
                                <Form className={classes.root}>
                                    <Grid container spacing={2}>
                                        <Grid size={12}>
                                            <Typography align="center">
                                                {t('AUTH_ALREADY_HAVE_ACCOUNT') + ' '}
                                                <Link to={getParsedPath(ROUTES.USER.LOGIN)}>
                                                    {t('ONBOARDING_SIGN_IN')}
                                                </Link>
                                            </Typography>
                                        </Grid>
                                        <GoogleSSO
                                            onSignInError={handleSSOError}
                                            onSignInSuccess={handleSSOSuccess}
                                            onStart={handleSSOStart}
                                        />
                                        <AppleSSO
                                            onSignInError={handleSSOError}
                                            onSignInSuccess={handleSSOSuccess}
                                        />
                                        {(settings?.appleSSOEnabled || settings?.googleSSOEnabled) && (
                                            <Grid size={12}>
                                                <Box className={classes.orContainer}>
                                                    <Divider />
                                                    <Typography className={classes.or}>OR</Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                        <Grid size={6}>
                                            <TextFormField
                                                name="firstName"
                                                label={t('ONBOARDING_SIGNUP_FIRSTNAME')}
                                                trimWhiteSpaces="sides"
                                            />
                                        </Grid>
                                        <Grid size={6}>
                                            <TextFormField
                                                name="lastName"
                                                label={t('ONBOARDING_SIGNUP_LASTNAME')}
                                                trimWhiteSpaces="sides"
                                            />
                                        </Grid>
                                        <Grid size={12}>
                                            <TextFormField
                                                name="identity"
                                                type={identityLabel}
                                                label={t(
                                                    `ONBOARDING_SIGNUP_LABEL_${identityLabel.toUpperCase()}`
                                                )}
                                                trimWhiteSpaces="sides"
                                                clearable
                                                clearAriaLabel="Clear"
                                                InputProps={{
                                                    autoComplete: 'email',
                                                    ...(showCountryInfo && {
                                                        startAdornment: countryPicker
                                                    })
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={12}>
                                            <PasswordFormField
                                                name="password"
                                                label={t('ONBOARDING_SIGNIN_PASSWORD')}
                                                onKeyDown={handleKeyPress(submitForm)}
                                            />
                                        </Grid>
                                        <Grid size={12}>
                                            <PasswordFormField
                                                name="confirmPassword"
                                                label={t('SIGN_UP_REPEAT_PASSWORD')}
                                                onKeyDown={handleKeyPress(submitForm)}
                                            />
                                        </Grid>
                                        <Grid size={12}>
                                            <TitleTypography align="center" variant="body1">
                                                {t('GDPR_PAGE_TITLE')}
                                            </TitleTypography>
                                            <Typography>
                                                {t('GDPR_BODY', {
                                                    applicationName: settings?.app?.bundleDisplayName || ''
                                                })}
                                            </Typography>
                                        </Grid>
                                        <Grid size={12}>
                                            {(settings?.app?.registrationShowMarketingOptIn ?? true) && (
                                                <Box paddingLeft={2}>
                                                    <CheckboxFormField
                                                        name="hasAgreedToReceiveMarketing"
                                                        label={t('GDPR_MARKETING_CHECKBOX_TITLE')}
                                                    />
                                                </Box>
                                            )}
                                            <Box paddingLeft={2}>
                                                <CheckboxFormField
                                                    name="hasAgreedToShareData"
                                                    label={t('GDPR_DATA_CHECKBOX_TITLE')}
                                                    color="primary"
                                                />
                                            </Box>
                                        </Grid>
                                        {!!registerError && (
                                            <Grid size={12}>
                                                <Typography color="error">{registerError}</Typography>
                                            </Grid>
                                        )}
                                        <Grid size={12}>
                                            {!hasAgreedToShareData && (
                                                <Tooltip title={t('AUTH_FORM_SIGNUP_TIP') || ''}>
                                                    <div>
                                                        <LoadingButton
                                                            variant="contained"
                                                            color="primary"
                                                            disabled={true}
                                                            fullWidth
                                                            onClick={submitForm}
                                                            className={classes.button}
                                                            loading={isSubmitting}
                                                        >
                                                            {t('AUTH_FORM_SIGNUP')}
                                                        </LoadingButton>
                                                    </div>
                                                </Tooltip>
                                            )}
                                            {hasAgreedToShareData && (
                                                <LoadingButton
                                                    variant="contained"
                                                    color="primary"
                                                    disabled={!isValid || isSubmitting || SSOInProgress}
                                                    fullWidth
                                                    onClick={submitForm}
                                                    className={classes.button}
                                                    loading={isSubmitting || SSOInProgress}
                                                >
                                                    {t('AUTH_FORM_SIGNUP')}
                                                </LoadingButton>
                                            )}
                                        </Grid>
                                        <Grid size={12}>
                                            <TermsAndConditions />
                                        </Grid>
                                    </Grid>
                                </Form>
                            );
                        }}
                    </Formik>
                </>
            )}
        </Root>
    );
};
